import { Component } from '@esengine/ecs-framework';
import { TaskStatus } from '../../behaviourTree/TaskStatus';
/**
 * 行为树组件
 * 将行为树功能集成到ECS实体中
 */
export class BehaviorTreeComponent extends Component {
    constructor() {
        super(...arguments);
        this._behaviorTree = null;
        this._isRunning = false;
        this._lastStatus = TaskStatus.Invalid;
        this._pausedTime = 0;
        this._totalRunTime = 0;
        /** 行为树更新间隔（秒），0表示每帧更新 */
        this.updateInterval = 0.1;
        /** 是否启用调试模式 */
        this.debugMode = false;
        /** 行为树名称（用于调试） */
        this.treeName = '';
        /** 执行统计信息 */
        this.stats = {
            totalTicks: 0,
            successCount: 0,
            failureCount: 0,
            runningCount: 0,
            averageTickTime: 0,
            lastTickTime: 0
        };
    }
    /**
     * 设置行为树
     */
    setBehaviorTree(tree) {
        if (this._behaviorTree) {
            this._behaviorTree.reset();
        }
        this._behaviorTree = tree;
        this._behaviorTree.setContext(this.entity);
        this._lastStatus = TaskStatus.Invalid;
        this.resetStats();
    }
    /**
     * 获取行为树
     */
    getBehaviorTree() {
        return this._behaviorTree;
    }
    /**
     * 启动行为树
     */
    start() {
        if (this._behaviorTree && !this._isRunning) {
            this._isRunning = true;
            this._behaviorTree.reset();
            if (this.debugMode) {
                console.log(`[${this.treeName}] 行为树启动 - 实体ID: ${this.entity.id}`);
            }
        }
    }
    /**
     * 停止行为树
     */
    stop() {
        this._isRunning = false;
        if (this._behaviorTree) {
            this._behaviorTree.reset();
        }
        if (this.debugMode) {
            console.log(`[${this.treeName}] 行为树停止 - 实体ID: ${this.entity.id}`);
        }
    }
    /**
     * 暂停行为树
     */
    pause() {
        if (this._isRunning) {
            this._isRunning = false;
            this._pausedTime = performance.now();
            if (this.debugMode) {
                console.log(`[${this.treeName}] 行为树暂停 - 实体ID: ${this.entity.id}`);
            }
        }
    }
    /**
     * 恢复行为树
     */
    resume() {
        if (!this._isRunning && this._behaviorTree) {
            this._isRunning = true;
            if (this._pausedTime > 0) {
                this._totalRunTime += performance.now() - this._pausedTime;
                this._pausedTime = 0;
            }
            if (this.debugMode) {
                console.log(`[${this.treeName}] 行为树恢复 - 实体ID: ${this.entity.id}`);
            }
        }
    }
    /**
     * 获取运行状态
     */
    get isRunning() {
        return this._isRunning && this.enabled && this.entity.activeInHierarchy;
    }
    /**
     * 获取最后执行状态
     */
    get lastStatus() {
        return this._lastStatus;
    }
    /**
     * 获取总运行时间（毫秒）
     */
    get totalRunTime() {
        return this._totalRunTime + (this._isRunning ? performance.now() - this._pausedTime : 0);
    }
    /**
     * 更新行为树（由BehaviorTreeSystem调用）
     */
    updateBehaviorTree(deltaTime) {
        if (!this._behaviorTree || !this.isRunning) {
            return;
        }
        const tickStartTime = performance.now();
        try {
            // 确保上下文是最新的
            this._behaviorTree.setContext(this.entity);
            // 执行行为树
            this._behaviorTree.tick(deltaTime);
            this._lastStatus = this._behaviorTree.getRoot().status;
            // 更新统计信息
            this.updateStats(tickStartTime);
            if (this.debugMode) {
                console.log(`[${this.treeName}] 实体 ${this.entity.id}: ${TaskStatus[this._lastStatus]} (${this.stats.lastTickTime.toFixed(2)}ms)`);
            }
        }
        catch (error) {
            console.error(`行为树执行错误 [${this.treeName}] 实体 ${this.entity.id}:`, error);
            this._lastStatus = TaskStatus.Failure;
            this.stats.failureCount++;
        }
    }
    /**
     * 更新统计信息
     */
    updateStats(startTime) {
        const tickTime = performance.now() - startTime;
        this.stats.totalTicks++;
        this.stats.lastTickTime = tickTime;
        this.stats.averageTickTime =
            (this.stats.averageTickTime * (this.stats.totalTicks - 1) + tickTime) / this.stats.totalTicks;
        switch (this._lastStatus) {
            case TaskStatus.Success:
                this.stats.successCount++;
                break;
            case TaskStatus.Failure:
                this.stats.failureCount++;
                break;
            case TaskStatus.Running:
                this.stats.runningCount++;
                break;
        }
    }
    /**
     * 重置统计信息
     */
    resetStats() {
        this.stats.totalTicks = 0;
        this.stats.successCount = 0;
        this.stats.failureCount = 0;
        this.stats.runningCount = 0;
        this.stats.averageTickTime = 0;
        this.stats.lastTickTime = 0;
        this._totalRunTime = 0;
        this._pausedTime = 0;
    }
    /**
     * 获取详细的性能统计信息
     */
    getPerformanceReport() {
        return {
            treeName: this.treeName,
            entityId: this.entity.id,
            isRunning: this.isRunning,
            lastStatus: TaskStatus[this._lastStatus],
            totalRunTime: this.totalRunTime,
            stats: Object.assign({}, this.stats),
            behaviorTreeStats: this._behaviorTree?.getStats()
        };
    }
    onAddedToEntity() {
        super.onAddedToEntity();
        this.treeName = this.treeName || `BehaviorTree_${this.entity.name}_${this.entity.id}`;
        if (this.debugMode) {
            console.log(`[${this.treeName}] 行为树组件添加到实体 ${this.entity.id}`);
        }
    }
    onRemovedFromEntity() {
        this.stop();
        if (this.debugMode) {
            console.log(`[${this.treeName}] 行为树组件从实体 ${this.entity.id} 移除`);
        }
        super.onRemovedFromEntity();
    }
    onEnabled() {
        super.onEnabled();
        if (this._behaviorTree && this.debugMode) {
            console.log(`[${this.treeName}] 行为树组件启用 - 实体 ${this.entity.id}`);
        }
    }
    onDisabled() {
        if (this.debugMode) {
            console.log(`[${this.treeName}] 行为树组件禁用 - 实体 ${this.entity.id}`);
        }
        super.onDisabled();
    }
}
//# sourceMappingURL=BehaviorTreeComponent.js.map